import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SearchIcon from '@mui/icons-material/Search'
import { useTheme } from '@mui/material/styles'
import { useParams } from 'react-router-dom'
import {
  useTransformsForAdaptations,
  usePropertyAdaptations,
  useAddPropertyAdaptation,
  useDeletePropertyAdaptation,
} from '../hooks/climateAnalytics'
import type { Transform } from '../hooks/climateAnalytics'
import { useClimateHazards } from '../hooks/analytics'
import { useSnackbar } from 'notistack'
import { HazardInfo } from '../types/climate'
import type { Adaptation } from '../types/adaptations'
import { ClimateAnalyticsPermissions, usePermissions } from 'balkerne-core/roles'
import { climateHazardViewConfigByCode } from '../components/climateAnalytics/common/hazardConfig'

type PendingAdaptationAction =
  | { type: 'attach'; transform: Transform }
  | { type: 'detach'; adaptation: Adaptation }

function PropertyClimateMitigations() {
  const theme = useTheme()
  const { id: propertyId } = useParams()
  const { data: hazards, isLoading: loadingHazards } = useClimateHazards()
  const [hazard, setHazard] = useState<HazardInfo | null>(null)
  const [searchText, setSearchText] = useState<string>('')
  const [openStub, setOpenStub] = useState(false)
  const [pendingAction, setPendingAction] = useState<PendingAdaptationAction | null>(null)
  const { data: transformsForAdaptations, isLoading: isTransformsLoading } = useTransformsForAdaptations(hazard?.id)
  const { data: assignedAdaptations, isLoading: isAdaptationsLoading } = usePropertyAdaptations(propertyId, hazard?.id)
  const { enqueueSnackbar } = useSnackbar()
  const { possible } = usePermissions()

  const hazardsWithAdaptations = useMemo(
    () => hazards.filter(_hazard => climateHazardViewConfigByCode[_hazard.name.toLowerCase()]?.supportsAdaptations),
    [hazards],
  )

  // Default to first hazard
  useEffect(() => {
    if (hazardsWithAdaptations.length === 0) {
      if (hazard) {
        setHazard(null)
      }
      return
    }

    const selectedHazardIsSupported = hazard
      ? hazardsWithAdaptations.some(_hazard => _hazard.id === hazard.id)
      : false
    if (!selectedHazardIsSupported) {
      setHazard(hazardsWithAdaptations[0])
    }
  }, [hazard, hazardsWithAdaptations])

  const assignedTransformIds = useMemo(
    () => new Set(assignedAdaptations.map(a => a.transform.id)),
    [assignedAdaptations],
  )

  // Filter transforms by search text and exclude already assigned
  const filteredTransformsForAdaptations = useMemo(() => {
    const unassignedTransformsForAdaptations = transformsForAdaptations.filter(
      transform => !assignedTransformIds.has(transform.id),
    )
    const searchQuery = searchText.trim().toLowerCase()
    if (!searchQuery) return unassignedTransformsForAdaptations
    return unassignedTransformsForAdaptations.filter(transform =>
      [transform.label, transform.description].some(attribute =>
        String(attribute ?? '')
          .toLowerCase()
          .includes(searchQuery),
      ),
    )
  }, [assignedTransformIds, transformsForAdaptations, searchText])

  const { mutate: attachAdaptation, isLoading: attaching } = useAddPropertyAdaptation({
    onSuccess: () => enqueueSnackbar('Adaptation attached', { variant: 'success' }),
  })
  const { mutate: removeAdaptation, isLoading: removing } = useDeletePropertyAdaptation({
    onSuccess: () => enqueueSnackbar('Adaptation removed', { variant: 'info' }),
  })
  const isSubmitting = attaching || removing
  const pendingAdaptationName =
    pendingAction?.type === 'attach' ? pendingAction.transform.label : pendingAction?.adaptation.transform.label

  const closePendingDialog = () => {
    if (!isSubmitting) {
      setPendingAction(null)
    }
  }

  const confirmPendingAction = () => {
    if (!pendingAction || !propertyId) return

    if (pendingAction.type === 'attach') {
      attachAdaptation(
        { propertyId, transformId: pendingAction.transform.id },
        { onSettled: () => setPendingAction(null) },
      )
      return
    }

    removeAdaptation(
      { propertyId, adaptationId: pendingAction.adaptation.id },
      { onSettled: () => setPendingAction(null) },
    )
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100%' }}>
      {/* Sticky header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar + 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'saturate(180%) blur(6px)',
        }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
            <Stack spacing={0.25}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                Climate Adaptations
              </Typography>
            </Stack>

            <Box flexGrow={1} />

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="hazard-label">Hazard</InputLabel>
                <Select
                  labelId="hazard-label"
                  value={hazard?.id || -1}
                  label="Hazard"
                  disabled={loadingHazards || hazardsWithAdaptations.length === 0}
                  onChange={e => {
                    const selected = hazardsWithAdaptations.find(_hazard => _hazard.id === e.target.value)
                    if (selected) {
                      setHazard(selected)
                    }
                  }}>
                  {hazardsWithAdaptations.map(_hazard => (
                    <MenuItem key={_hazard.id} value={_hazard.id}>
                      {_hazard.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Search adaptations"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              {/* <PermissionGate action={ClimateAnalyticsPermissions.Modify}>
                <Tooltip title="Create a new adaptation (stub)">
                  <span>
                    <Button variant="outlined" startIcon={<AddIcon />} disabled onClick={() => setOpenStub(true)}>
                      New adaptation
                    </Button>
                  </span>
                </Tooltip>
              </PermissionGate> */}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        {/* Loading State */}
        {(isTransformsLoading || isAdaptationsLoading) && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Stack>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {/* Available transforms */}
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Available ({filteredTransformsForAdaptations.length})
              </Typography>
            </Stack>
            <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {filteredTransformsForAdaptations.map(transform => {
                return (
                  <Fragment key={transform.id}>
                    <ListItem sx={{ alignItems: 'flex-start', gap: 2, px: 2, py: 1.5 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{transform.label}</Typography>
                        {transform.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                            {transform.description}
                          </Typography>
                        )}
                      </Box>
                      <Tooltip
                        title={
                          possible(ClimateAnalyticsPermissions.Modify)
                            ? ''
                            : 'You do not have permission to attach adaptations'
                        }>
                        <Box sx={{ flexShrink: 0, pt: 0.25 }}>
                          <Button
                            size="small"
                            disabled={possible(ClimateAnalyticsPermissions.Modify) ? isSubmitting : true}
                            aria-disabled={!possible(ClimateAnalyticsPermissions.Modify)}
                            variant="contained"
                            onClick={() => setPendingAction({ type: 'attach', transform })}>
                            Attach
                          </Button>
                        </Box>
                      </Tooltip>
                    </ListItem>
                    <Divider component="li" />
                  </Fragment>
                )
              })}
              {filteredTransformsForAdaptations.length === 0 && (
                <ListItem>
                  <Typography color="text.secondary">No adaptations found</Typography>
                </ListItem>
              )}
            </List>
          </Box>

          {/* Assigned adaptations */}
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Assigned ({assignedAdaptations.length})
              </Typography>
            </Stack>
            <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {assignedAdaptations.map(adapt => (
                <Fragment key={adapt.id}>
                  <ListItem sx={{ alignItems: 'flex-start', gap: 2, px: 2, py: 1.5 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{adapt.transform.label}</Typography>
                      {adapt.transform.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                          {adapt.transform.description}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip
                      title={
                        possible(ClimateAnalyticsPermissions.Modify)
                          ? ''
                          : 'You do not have permission to remove adaptations'
                      }>
                      <Box sx={{ flexShrink: 0 }}>
                        <IconButton
                          color="error"
                          disabled={possible(ClimateAnalyticsPermissions.Modify) ? isSubmitting : true}
                          aria-disabled={!possible(ClimateAnalyticsPermissions.Modify)}
                          onClick={() => setPendingAction({ type: 'detach', adaptation: adapt })}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  </ListItem>
                  <Divider component="li" />
                </Fragment>
              ))}
              {assignedAdaptations.length === 0 && (
                <ListItem>
                  <Typography color="text.secondary">No adaptations assigned</Typography>
                </ListItem>
              )}
            </List>
          </Box>
        </Stack>
      </Container>

      <Dialog open={Boolean(pendingAction)} onClose={closePendingDialog} fullWidth maxWidth="sm">
        <DialogTitle>{pendingAction?.type === 'attach' ? 'Attach adaptation?' : 'Remove adaptation?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingAction?.type === 'attach'
              ? `After attaching "${pendingAdaptationName}", the climate impact model will recalculate this property's results.`
              : `After removing "${pendingAdaptationName}", the climate impact model will recalculate this property's results.`}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Updated results usually appear within a few seconds, but may take up to a minute.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePendingDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pendingAction?.type === 'detach' ? 'error' : 'primary'}
            onClick={confirmPendingAction}
            disabled={isSubmitting}>
            {isSubmitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : pendingAction?.type === 'detach' ? (
              'Remove'
            ) : (
              'Attach'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog | Create Adaptation  */}
      <Dialog open={openStub} onClose={() => setOpenStub(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create adaptation (stub)</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This is a placeholder. Once the creation API is exposed, this dialog will allow creating organisation-scoped
            adaptation transforms (e.g., multiplicative <code>ymul</code> with a<code>constant_function</code> value
            like 0.8 for 20% reduction).
          </DialogContentText>

          <Stack spacing={2}>
            <FormControl size="small">
              <InputLabel id="stub-hazard">Hazard</InputLabel>
              <Select labelId="stub-hazard" label="Hazard" value={hazard?.id || -1} disabled>
                {hazards.map(_hazard => (
                  <MenuItem key={_hazard.id} value={_hazard.id}>
                    {_hazard.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Slot family"
              size="small"
              value="ymul"
              disabled
              helperText="Adaptations usually use ymul"
            />
            <TextField label="Shape type" size="small" value="constant_function" disabled />
            <TextField label="Value (e.g., 0.8)" size="small" placeholder="0.8" disabled />
            <TextField label="Label" size="small" placeholder="e.g., Flood protection (20% reduction)" disabled />
            <TextField label="Description" size="small" placeholder="Optional" disabled />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStub(false)}>Close</Button>
          <Button variant="contained" disabled>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PropertyClimateMitigations
